/**
 * React binding for lib/debouncedCommit — a continuous input's local
 * buffer with one debounced write per gesture.
 *
 * `value` is what the control renders: the uncommitted buffer while one
 * exists, else the live doc value. The buffer clears when the commit
 * settles — by then Firestore's latency compensation has already updated
 * the doc value, so success is seamless and failure visibly reverts.
 *
 * flush() commits a pending edit immediately — wire it to blur. Unmount
 * and pagehide (tab close / bfcache park) also flush, so an edit made
 * moments before navigating away still lands.
 */

import { useEffect, useRef, useState } from 'react';

import {
  createDebouncedCommit,
  type DebouncedCommitController,
} from '@/lib/debouncedCommit';

export function useDebouncedCommit<T>(
  docValue: T,
  commit: (v: T) => Promise<void>,
  debounceMs = 800,
): { value: T; set: (v: T) => void; flush: () => void } {
  const [pending, setPending] = useState<T | undefined>(undefined);

  // Latest commit callback without re-creating the controller.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const ctlRef = useRef<DebouncedCommitController<T> | null>(null);
  if (ctlRef.current === null) {
    ctlRef.current = createDebouncedCommit<T>({
      commit: (v) => commitRef.current(v),
      debounceMs,
      onPendingChange: setPending,
    });
  }
  const ctl = ctlRef.current;

  useEffect(() => {
    const flushNow = () => ctl.flush();
    window.addEventListener('pagehide', flushNow);
    return () => {
      window.removeEventListener('pagehide', flushNow);
      // Unmount: commit anything pending, then stop the timer.
      ctl.flush();
      ctl.dispose();
    };
  }, [ctl]);

  return {
    value: pending === undefined ? docValue : pending,
    set: (v: T) => {
      // An idle edit that equals the already-saved value needs no write —
      // and every account write costs a Cloud Function invocation (plus
      // possible TTS re-synthesis). Covers "Reset to defaults" while already
      // at defaults and re-selecting the current dropdown value. ONLY when
      // nothing is pending/in flight: with an uncommitted (or committing)
      // value, the "same" value is a genuine revert that must still win over
      // the in-flight write. The controller's synchronous hasPending() is
      // the truth here — the React-mirrored `pending` state lags within an
      // event-handler burst (two set() calls before a re-render would
      // wrongly skip the second). Object.is only matches primitives, so
      // object-valued fields (phrases) conservatively always commit.
      if (!ctl.hasPending() && Object.is(v, docValue)) return;
      ctl.set(v);
    },
    flush: () => ctl.flush(),
  };
}
